import { supabase, getAuthState } from '../auth';
import { seedAds } from './ads';

const FEED_FUNCTION_NAME = 'feed-data';
const FEED_LIMIT = 8;

// Persisted across full page loads (this is an MPA, so the module-level
// `cache` below is wiped on every navigation). Scoped per user because the
// payload carries a member-specific sponsor pool.
const FEED_CACHE_KEY = 'pw:feed-cache:v1';
const FEED_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

let cache = null;
const inflight = new Map();

function feedUrl() {
  const supabaseUrl = window.PINK_WALLET_CONFIG?.supabaseUrl;
  if (!supabaseUrl) return null;
  const baseUrl = supabaseUrl.replace('.supabase.co', '.functions.supabase.co');
  return `${baseUrl}/functions/v1/${FEED_FUNCTION_NAME}`;
}

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` };
  }
  return {};
}

async function requestFeed(offset, limit) {
  const url = feedUrl();
  if (!url) return null;

  const params = new URLSearchParams({ offset: String(offset) });
  if (limit) params.set('limit', String(limit));
  const fullUrl = `${url}?${params.toString()}`;

  const headers = await authHeaders();
  const response = await fetch(fullUrl, { headers });
  if (!response.ok) {
    throw new Error(`feed-data request failed with status ${response.status}`);
  }
  const data = await response.json();
  if (!data || data.error) {
    throw new Error(data?.error || 'feed-data returned an empty response');
  }
  return data;
}

function currentUserId() {
  try {
    return getAuthState()?.userId || 'anon';
  } catch (error) {
    return 'anon';
  }
}

function readPersisted() {
  try {
    const raw = localStorage.getItem(FEED_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.data) return null;
    return parsed;
  } catch (error) {
    return null;
  }
}

/** Apply the side effects shared by fresh and cached payloads. */
function applyFeed(data) {
  if (!data) return data;
  if (data.feed && data.feed.adPool) {
    seedAds(data.feed.adPool);
  }
  if (data.catalog) {
    window.PW_BOOKMARKS_CATALOG = data.catalog;
    window.dispatchEvent(new CustomEvent('pw:bookmarks-changed'));
  }
  return data;
}

function persist(data) {
  try {
    localStorage.setItem(FEED_CACHE_KEY, JSON.stringify({
      userId: currentUserId(),
      ts: Date.now(),
      data
    }));
  } catch (error) {
    // Private mode or quota exceeded — caching is best-effort.
  }
}

/** Return the last persisted page-0 payload for the current user, if any. */
function persistedForCurrentUser() {
  const persisted = readPersisted();
  if (persisted && persisted.userId === currentUserId()) {
    return persisted;
  }
  return null;
}

/**
 * Drop every layer of cached feed data. Called when the auth state changes so
 * a member's sponsor pool is never shown to an anonymous reader (or vice
 * versa). Safe to call from anywhere.
 */
export function clearFeedCache() {
  cache = null;
  inflight.clear();
  try {
    localStorage.removeItem(FEED_CACHE_KEY);
  } catch (error) {
    // Ignore storage errors.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('authStateChanged', clearFeedCache);
}

/**
 * Load the front-page feed (stories, upcoming events and the bookmarks
 * catalog) from the feed-data edge function. Shared across the Stories and
 * UpcomingEvents islands so the edge function is hit once per page load.
 *
 * Results are memoized in memory for the current page and persisted to
 * localStorage for 10 minutes so repeat navigations render instantly. A stale
 * persisted payload is still used as an offline/error fallback.
 * Populates window.PW_BOOKMARKS_CATALOG when loaded.
 * @returns {Promise<object|null>} The feed payload, or null on failure.
 */
export async function fetchFeed({ force = false } = {}) {
  if (!force && cache) return applyFeed(cache);

  if (!force) {
    const persisted = persistedForCurrentUser();
    if (persisted) {
      if (Date.now() - persisted.ts < FEED_CACHE_TTL_MS) {
        cache = persisted.data;
        return applyFeed(cache);
      }
    }
  }

  if (inflight.has(0)) return inflight.get(0);

  const promise = (async () => {
    try {
      const data = await requestFeed(0, FEED_LIMIT);
      if (!data) return applyFeed(fallback());
      cache = data;
      persist(data);
      return applyFeed(data);
    } catch (error) {
      console.error('[feed] Failed to load front-page feed:', error);
      return applyFeed(fallback());
    } finally {
      inflight.delete(0);
    }
  })();

  inflight.set(0, promise);
  return promise;
}

/** Stale persisted payload for the current user, or null. */
function fallback() {
  const persisted = persistedForCurrentUser();
  if (!persisted) return null;
  cache = persisted.data;
  return cache;
}

/**
 * Load a later page of the feed for infinite scroll. Page 0 is served from
 * the shared cache. Each offset is requested at most once at a time; the
 * `hasMore` flag on feed tells the caller whether to keep scrolling.
 * @param {{ offset?: number, limit?: number, force?: boolean }} options
 * @returns {Promise<object|null>} The feed payload for that page, or null on failure.
 */
export async function fetchFeedPage({ offset = 0, limit = FEED_LIMIT, force = false } = {}) {
  if (offset === 0) {
    return fetchFeed({ force });
  }
  if (inflight.has(offset)) return inflight.get(offset);

  const promise = (async () => {
    try {
      return await requestFeed(offset, limit);
    } catch (error) {
      console.error(`[feed] Failed to load feed page ${offset}:`, error);
      return null;
    } finally {
      inflight.delete(offset);
    }
  })();

  inflight.set(offset, promise);
  return promise;
}
